import { BarChart } from "@mui/icons-material";
import { TabContext, TabList } from "@mui/lab";
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import * as React from "react";
import { useTranslation } from "react-i18next";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const dummy_data = {
  patient: {
    name: "Axy",
    species: "Dog",
    breed: "Mješanac",
    sex: "Female",
    status: "Spayed",
    owner: "Ivana Vuković",
  },
  tests: [
    {
      testName: "VETSCAN VS2 - Thyroxine (T4) / Cholesterol Test",
      date: "2024-01-22T09:11:00",
      results: [
        {
          parameter: "T4",
          value: 48,
          unit: "nmol/L",
          reference: "14 - 52",
          flag: "Normal",
        },
        {
          parameter: "CHOL",
          value: 4.6,
          unit: "mmol/L",
          reference: "3.2 - 7",
          flag: "Normal",
        },
      ],
      notes: { HEM: "1+", LIP: "2+", ICT: "0" },
    },
    {
      testName: "VETSCAN VS2 - Comprehensive Diagnostic",
      date: "2024-01-22T08:52:00",
      results: [
        {
          parameter: "ALB",
          value: 43,
          unit: "g/L",
          reference: "25 - 44",
          flag: "Normal",
        },
        {
          parameter: "ALP",
          value: 48,
          unit: "U/L",
          reference: "20 - 150",
          flag: "Normal",
        },
        {
          parameter: "ALT",
          value: 72,
          unit: "U/L",
          reference: "10 - 118",
          flag: "Normal",
        },
        {
          parameter: "AMY",
          value: 916,
          unit: "U/L",
          reference: "200 - 1200",
          flag: "Normal",
        },
        {
          parameter: "TBIL",
          value: 4,
          unit: "µmol/L",
          reference: "2 - 10",
          flag: "Normal",
        },
        {
          parameter: "BUN",
          value: 10.8,
          unit: "mmol/L",
          reference: "2.5 - 8.9",
          flag: "High",
        },
        {
          parameter: "CA",
          value: 2.86,
          unit: "mmol/L",
          reference: "2.15 - 2.95",
          flag: "Normal",
        },
        {
          parameter: "PHOS",
          value: 1.49,
          unit: "mmol/L",
          reference: "0.94 - 2.13",
          flag: "Normal",
        },
        {
          parameter: "CRE",
          value: 62,
          unit: "µmol/L",
          reference: "27 - 124",
          flag: "Normal",
        },
        {
          parameter: "GLU",
          value: 5.8,
          unit: "mmol/L",
          reference: "3.3 - 6.1",
          flag: "Normal",
        },
        {
          parameter: "NA+",
          value: 157,
          unit: "mmol/L",
          reference: "138 - 160",
          flag: "Normal",
        },
        {
          parameter: "K+",
          value: 4.2,
          unit: "mmol/L",
          reference: "3.7 - 5.8",
          flag: "Normal",
        },
        {
          parameter: "TP",
          value: 71,
          unit: "g/L",
          reference: "54 - 82",
          flag: "Normal",
        },
        {
          parameter: "GLOB",
          value: 28,
          unit: "g/L",
          reference: "23 - 52",
          flag: "Normal",
        },
      ],
      notes: { HEM: "0", LIP: "1+", ICT: "0" },
    },
    {
      testName: "VETSCAN HM5c - Hematology",
      date: "2024-01-22T08:47:00",
      results: [
        {
          parameter: "WBC",
          value: 11.89,
          unit: "10^9/L",
          reference: "6 - 17",
          flag: "Normal",
        },
        {
          parameter: "LYM",
          value: 3.19,
          unit: "10^9/L",
          reference: "1 - 4.8",
          flag: "Normal",
        },
        {
          parameter: "MON",
          value: 0.29,
          unit: "10^9/L",
          reference: "0.2 - 1.5",
          flag: "Normal",
        },
        {
          parameter: "NEU",
          value: 8.0,
          unit: "10^9/L",
          reference: "3 - 12",
          flag: "Normal",
        },
        {
          parameter: "EOS",
          value: 0.31,
          unit: "10^9/L",
          reference: "0 - 0.8",
          flag: "Normal",
        },
        {
          parameter: "BAS",
          value: 0.1,
          unit: "10^9/L",
          reference: "0 - 0.4",
          flag: "Normal",
        },
        {
          parameter: "RBC",
          value: 9.11,
          unit: "10^12/L",
          reference: "5.5 - 8.5",
          flag: "High",
        },
        {
          parameter: "HGB",
          value: 19.4,
          unit: "g/dL",
          reference: "12 - 18",
          flag: "High",
        },
        {
          parameter: "HCT",
          value: 55.54,
          unit: "%",
          reference: "37 - 55",
          flag: "High",
        },
        {
          parameter: "MCV",
          value: 61,
          unit: "fL",
          reference: "60 - 77",
          flag: "Normal",
        },
        {
          parameter: "MCH",
          value: 21.3,
          unit: "pg",
          reference: "19.5 - 24.5",
          flag: "Normal",
        },
        {
          parameter: "MCHC",
          value: 35.0,
          unit: "g/dL",
          reference: "31 - 39",
          flag: "Normal",
        },
        {
          parameter: "RDWc",
          value: 20.2,
          unit: "%",
          reference: "14 - 20",
          flag: "High",
        },
        {
          parameter: "PLT",
          value: 291,
          unit: "10^9/L",
          reference: "165 - 500",
          flag: "Normal",
        },
        {
          parameter: "MPV",
          value: 11.4,
          unit: "fL",
          reference: "3.9 - 11.1",
          flag: "High",
        },
      ],
    },
  ],
};

function TestResultTable({ test }) {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFlagColor = (flag) => {
    switch (flag) {
      case "High":
        return "#f44336";
      case "Low":
        return "#ff9800";
      case "Normal":
        return "#4caf50";
      default:
        return "#757575";
    }
  };

  const parseReferenceRange = (reference) => {
    const [min, max] = reference.split(" - ").map(Number);
    return { min, max };
  };

  const calculateProgressValue = (value, reference) => {
    const { min, max } = parseReferenceRange(reference);
    const range = max - min;
    const position = ((value - min) / range) * 100;
    return Math.min(Math.max(position, 0), 100);
  };

  const getProgressColor = (value, reference, flag) => {
    if (flag === "High") return "#f44336";
    if (flag === "Low") return "#ff9800";
    return "#4caf50";
  };

  return (
    <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 3 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          {test.testName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatDate(test.date)}
        </Typography>
        {test.notes && (
          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
            <strong>{t("NotesLabel")}:</strong>{" "}
            {Object.entries(test.notes)
              .map(([key, value]) => `${key}: ${value}`)
              .join(", ")}
          </Typography>
        )}
      </Box>

      <Table sx={{ minWidth: 650 }} aria-label="test results table">
        <TableHead>
          <TableRow sx={{ backgroundColor: "primary.light" }}>
            <TableCell sx={{ fontWeight: "bold", color: "white" }}>
              {t("TestColumn")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "white" }}>
              {t("DateColumn")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "white" }}>
              {t("ReferenceIntervalColumn")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "white" }}>
              {t("GraphColumn")}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "white" }}>
              {t("StatusColumn")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {test.results.map((result, idx) => {
            const progressValue = calculateProgressValue(result.value, result.reference);
            const progressColor = getProgressColor(result.value, result.reference, result.flag);

            return (
              <TableRow
                key={idx}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <TableCell component="th" scope="row">
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {result.parameter}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {result.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {result.unit}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{formatDate(test.date)}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{result.reference}</Typography>
                </TableCell>

                <TableCell sx={{ width: 200 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 30, fontSize: "0.75rem" }}>
                      {parseReferenceRange(result.reference).min}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "#e0e0e0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: progressColor,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 30, fontSize: "0.75rem" }}>
                      {parseReferenceRange(result.reference).max}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
                    {t("CurrentLabel")}: {result.value}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={result.flag}
                    size="small"
                    sx={{
                      backgroundColor: getFlagColor(result.flag),
                      color: "white",
                      textAlign: "center",
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MedicalGraphs() {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(0);

  const tab_labels = {
    1: t("ThyroxineCholesterolTest"),
    2: t("ComprehensiveDiagnostic"),
    3: t("Hematology"),
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, boxShadow: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabContext value={value}>
            <TabList
              variant="scrollable"
              scrollButtons
              value={value}
              onChange={handleChange}
              aria-label="medical test tabs"
              sx={{
                "& .MuiTab-root": { fontWeight: "bold", textTransform: "none", fontSize: "1rem" },
              }}
            >
              <Tab label={tab_labels[1]} value={0} icon={<BarChart />} iconPosition="start" />
              <Tab label={tab_labels[2]} value={1} icon={<BarChart />} iconPosition="start" />
              <Tab label={tab_labels[3]} value={2} icon={<BarChart />} iconPosition="start" />
            </TabList>
          </TabContext>
        </Box>

        <CustomTabPanel value={value} index={0}>
          <TestResultTable test={dummy_data.tests[0]} />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <TestResultTable test={dummy_data.tests[1]} />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          <TestResultTable test={dummy_data.tests[2]} />
        </CustomTabPanel>
      </Paper>
    </Box>
  );
}
